import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SharedRequests, TrialLockedMemory } from 'src/app/utils/shared-requests';

/**
 * Lecturas del dashboard de empresa que piden varias piezas en la misma carga
 * (la empresa, sus sedes, su suscripción, los recuentos de empleados y las
 * píldoras de Inicio). Cada una se guarda durante la navegación: se olvida en
 * cada `NavigationStart` y tras cualquier escritura propia a la API
 * (`ForgetReadsInterceptor`). Un 403 `trial_locked` se recuerda por empresa
 * hasta que se desbloquea (`forgetLocked`, lo llama `CorporationTrialService`):
 * no se vuelve a pedir al navegar dentro de la empresa bloqueada.
 */
@Injectable({ providedIn: 'root' })
export class DashboardReadsService implements TrialLockedMemory {
  private readonly caches: SharedRequests<unknown>[] = [];
  private readonly locked = new Map<string, Map<string, HttpErrorResponse>>();

  constructor(private router: Router) {
    router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => this.forget());
  }

  create<T>(name: string): SharedRequests<T> {
    const cache = new SharedRequests<T>(name, this);
    this.caches.push(cache as SharedRequests<unknown>);
    return cache;
  }

  /** La empresa de la página abierta (`/corporation/:id/...`), o `null`. */
  openCorporationId(): string | null {
    const match = this.router.url.match(/^\/corporation\/([0-9a-f-]{36})(?:[/?#]|$)/i);
    return match ? match[1] : null;
  }

  /** Olvida lo guardado (no los 403 `trial_locked`). */
  forget(): void {
    this.caches.forEach((cache) => cache.clear());
  }

  lockedError(corporationId: string, key: string): HttpErrorResponse | undefined {
    return this.locked.get(corporationId)?.get(key);
  }

  rememberLocked(corporationId: string, key: string, error: HttpErrorResponse): void {
    let errors = this.locked.get(corporationId);
    if (!errors) {
      errors = new Map();
      this.locked.set(corporationId, errors);
    }
    errors.set(key, error);
  }

  /** La empresa ya no está bloqueada: lo que dio 403 `trial_locked` se vuelve a pedir. */
  forgetLocked(corporationId: string): void {
    this.locked.delete(corporationId);
  }
}
